import {
  TeamOutlined,
  HeartOutlined,
  BellOutlined,
  PoweroffOutlined,
  MessageOutlined,
  CompassOutlined,
  FireOutlined,
  ApartmentOutlined,
} from "@ant-design/icons";
import Layout from "./Layout";
import React from "react";
import { Typography } from "antd";
import Support from "../components/Support/Support";
import Homepage from "../components/Home/Homepage";
import BrowseEdges from "../components/Home/BrowseEdges";
import MyRelationships from "../components/MyLists/MyRelationships";
import Entity from "../components/Entities/Entity";
import EntityCall from "../components/Hangouts/EntityCall";
import Logout from "../components/User/Logout";
import Messages from "../components/Messages/Messages";
import Notification from "../components/User/Notification";
import ProtectedRoute from "../components/ProtectedRoute";
import AppPage from "../components/Apps/AppPage";
import ConnectAgentPage from "../components/Account/ConnectAgentPage";
import GatheringPage from "../components/Gathering/GatheringPage";
import GatheringCall from "../components/Gathering/GatheringCall";
import GatheringFlow from "../components/Gathering/GatheringFlow";
import GatheringGate from "../components/GatheringGate";
import GatheringAccessGate from "../components/GatheringAccessGate";
import GuestJoinPage from "../components/Gathering/GuestJoinPage";
import InvitePage from "../components/Entities/InvitePage";
import ClaimPage from "../components/Entities/ClaimPage";
import PrivacyPolicy from "../components/Legal/PrivacyPolicy";
import TrustPage from "../components/Legal/TrustPage";
import ConsentsPage from "../components/Legal/ConsentsPage";
import TermsOfUse from "../components/Legal/TermsOfUse";
import Account from "../components/Account/Account";
import NodeAccount from "../components/Account/NodeAccount";
import NewNodePage from "../components/Node/NewNodePage";
import LegacyRoadmapPage from "../components/Roadmap/LegacyRoadmapPage";
import TestVizPage from "../components/Skargard/TestVizPage";

const { Text } = Typography;

const RouteMap = [
  {
    // Public invite page — no ProtectedRoute, accessible without login
    path: "/join/:token",
    element: <InvitePage />,
  },
  {
    // Public person-claim page — no ProtectedRoute; binds the logged-in
    // Keycloak account to a super-admin-created placeholder person entity.
    path: "/claim/:token",
    element: <ClaimPage />,
  },
  {
    // Public Gathering guest entry — no ProtectedRoute: a name + the host's
    // passcode mints a guest identity (utils/guest_session.js) that
    // GatheringAccessGate lets through to /gathering. Renders its own "not
    // available" state on nodes with feature_gathering_guest_access off.
    path: "/gathering/join",
    element: <GuestJoinPage />,
  },
  {
    // Public — no ProtectedRoute, needs to be readable pre-login.
    path: "/privacy",
    element: <PrivacyPolicy />,
  },
  {
    // Public — no ProtectedRoute, needs to be readable pre-login.
    path: "/trust",
    element: <TrustPage />,
  },
  {
    // Public — this node's consent wording (see ConsentsPage.jsx).
    path: "/consents",
    element: <ConsentsPage />,
  },
  {
    // Public — a generative piece from the skargard engine (vendored under
    // src/vendor/skargard). No ProtectedRoute and no Layout: it is a bare
    // stage, not an app screen, and gating it behind login would make it
    // awkward to open on a phone or a second screen.
    path: "/test_viz",
    element: <TestVizPage />,
  },
  {
    // Public — no ProtectedRoute, needs to be readable pre-login (also linked
    // from the Keycloak "Terms and Conditions" required-action screen).
    path: "/terms",
    element: <TermsOfUse />,
  },
  {
    // The homepage is browse; "/browse" below carries its nav entry.
    path: "/",
    element: (
      <ProtectedRoute>
        <Layout>
          <Homepage />
        </Layout>
      </ProtectedRoute>
    ),
    errorElement: <Text>Error</Text>,
  },
  {
    // Same homepage as "/", and the one that sits in the nav bar.
    path: "/browse",
    element: (
      <ProtectedRoute>
        <Layout>
          <Homepage />
        </Layout>
      </ProtectedRoute>
    ),
    errorElement: <Text>Error</Text>,
    placeinNavBar: true,
    name: "browse",
    icon: <CompassOutlined />,
  },
  {
    // Browse's sibling for the other half of the graph: the connections
    // (edges) between the entities (nodes) browse lists, in the same table —
    // tabs by state, column filters, the query bar and its ?q= link. NavBar
    // hides the entry on a node with no context or relationship type, since
    // nothing can be connected there.
    path: "/browse/edges",
    element: (
      <ProtectedRoute>
        <Layout>
          <BrowseEdges />
        </Layout>
      </ProtectedRoute>
    ),
    errorElement: <Text>Error</Text>,
    placeinNavBar: true,
    name: "browse edges",
    icon: <ApartmentOutlined />,
  },
  {
    // Gathering — node-level breakout rooms (groups people pick, an ideal
    // room, topics and a timed flow). Falls back to Homepage via
    // GatheringGate on nodes with feature_gathering off, and NavBar hides it.
    // No Layout here (same as /entity/:entityId) — the page is its own full
    // screen with no header/footer chrome; GatheringGate wraps its Homepage
    // fallback in Layout instead. GatheringAccessGate, not ProtectedRoute:
    // it also admits a guest who joined at /gathering/join.
    path: "/gathering",
    element: (
      <GatheringAccessGate>
        <GatheringGate>
          <GatheringPage />
        </GatheringGate>
      </GatheringAccessGate>
    ),
    errorElement: <Text>Error</Text>,
    placeinNavBar: true,
    name: "gathering",
    icon: <FireOutlined />,
  },
  {
    // Following the flow live — the right room for each step, with a
    // transition screen between them (GatheringFlow.jsx). Full screen, no
    // Layout; open to guests like /gathering.
    path: "/gathering/flow",
    element: (
      <GatheringAccessGate>
        <GatheringGate>
          <GatheringFlow />
        </GatheringGate>
      </GatheringAccessGate>
    ),
  },
  {
    // The gathering's video rooms (common + breakout) — see GatheringCall.jsx.
    // No Layout, same reasoning as /entity/:entityId/hangout below.
    path: "/gathering/call",
    element: (
      <GatheringAccessGate>
        <GatheringCall />
      </GatheringAccessGate>
    ),
  },

  {
    path: "/entity/:entityId",
    element: (
      <ProtectedRoute>
        <Entity />
      </ProtectedRoute>
    ),
  },
  {
    path: "/entity/:entityId/hangout",
    element: (
      <ProtectedRoute>
        <EntityCall />
      </ProtectedRoute>
    ),
  },
  {
    path: "/relationships",
    placeinNavBar: false,
    name: "relationships", // (0/7)
    element: (
      <ProtectedRoute>
        <Layout>
          <MyRelationships />
        </Layout>
      </ProtectedRoute>
    ),
    icon: <TeamOutlined />,
  },
  {
    path: "/messages",
    element: (
      <ProtectedRoute>
        <Layout>
          <Messages />
        </Layout>
      </ProtectedRoute>
    ),
    placeinNavBar: true,
    name: "messages",
    icon: <MessageOutlined />,
  },
  {
    path: "/messages/:entityId",
    element: (
      <ProtectedRoute>
        <Layout>
          <Messages />
        </Layout>
      </ProtectedRoute>
    ),
  },
  {
    // One installed app instance: a page, and later channels and rooms.
    path: "/app/:instanceId",
    element: (
      <ProtectedRoute>
        <Layout>
          <AppPage />
        </Layout>
      </ProtectedRoute>
    ),
    errorElement: <Text>Error</Text>,
  },
  {
    // OAuth consent for a connector such as the Claude app: /oauth/authorize
    // on the API sends the browser here (routers/oauth.py).
    path: "/connect",
    element: (
      <ProtectedRoute>
        <Layout>
          <ConnectAgentPage />
        </Layout>
      </ProtectedRoute>
    ),
    errorElement: <Text>Error</Text>,
  },
  {
    path: "/support",
    placeinNavBar: false,
    // name: "support us",
    element: (
      <ProtectedRoute>
        <Layout>
          <Support />
        </Layout>
      </ProtectedRoute>
    ),
    icon: <HeartOutlined />,
  },
  {
    path: "/notifications",
    placeinNavBar: false,
    // name: "notifications",
    element: (
      <ProtectedRoute>
        <Layout>
          <Notification />
        </Layout>
      </ProtectedRoute>
    ),
    icon: <BellOutlined />,
  },
  {
    path: "/account",
    placeinNavBar: false,
    element: (
      <ProtectedRoute>
        <Layout>
          <Account />
        </Layout>
      </ProtectedRoute>
    ),
  },
  {
    // Optional :tab segment = the active settings tab (see NodeAccount.jsx).
    path: "/node-account/:tab?",
    placeinNavBar: false,
    element: (
      <ProtectedRoute>
        <Layout>
          <NodeAccount />
        </Layout>
      </ProtectedRoute>
    ),
  },
  {
    // Self-service node creation (docs/self-service-node-creation-todo.md):
    // name a node, pick its address, become its superadmin. Served by the
    // platform node with feature_self_service_nodes on; elsewhere the page
    // points at the platform node. ?name= pre-fills the form (landing page).
    path: "/nodes/new",
    placeinNavBar: false,
    element: (
      <ProtectedRoute>
        <Layout>
          <NewNodePage />
        </Layout>
      </ProtectedRoute>
    ),
  },
  {
    // Archive of the old roadmap + intro carousel + product tour, flattened
    // into one page. Intentionally unlinked from the nav bar.
    path: "/legacy_roadmap",
    placeinNavBar: false,
    element: (
      <ProtectedRoute>
        <Layout>
          <LegacyRoadmapPage />
        </Layout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/logout",
    placeinNavBar: true,
    //name: "logout",
    element: <Logout />,
    icon: <PoweroffOutlined />,
  },
];

export default RouteMap;
